import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ContentV2ListItem } from '../types/studioContentV2Types'
import type { CardSlot } from '../pages/Studio/BoardStudio/types'

type BoardStudioState = {
  currentBoard: ContentV2ListItem | null
  /**
   * Working copy of the board's card tree.
   * Initialized from the API response on board open.
   * Updated on every card save/create.
   * Cleared when the user leaves the board.
   */
  slots: CardSlot[] | null
}

/**
 * Pure helper — mirrors updateLevelByPath in BoardStudioGrid.
 * Returns a new tree with the updater applied at the given path depth.
 */
function updateLevelByPath(
  tree: CardSlot[],
  path: string[],
  updater: (level: CardSlot[]) => CardSlot[],
): CardSlot[] {
  if (path.length === 0) return updater(tree)
  const [head, ...rest] = path
  return tree.map((slot) =>
    slot.id !== head
      ? slot
      : {
          ...slot,
          children: updateLevelByPath(slot.children ?? [], rest, updater),
        },
  )
}

const boardStudioSlice = createSlice({
  name: 'boardStudio',
  initialState: { currentBoard: null, slots: null } as BoardStudioState,
  reducers: {
    setCurrentBoard: (state, { payload }: PayloadAction<ContentV2ListItem>) => {
      state.currentBoard = payload
    },
    clearCurrentBoard: (state) => {
      state.currentBoard = null
    },

    /**
     * Called once when the board first opens.
     * Guards against re-initialisation on re-renders.
     */
    initBoardSlots: (state, { payload }: PayloadAction<CardSlot[]>) => {
      if (state.slots === null) {
        state.slots = payload as BoardStudioState['slots']
      }
    },

    /**
     * Replace the entire card tree (drag/drop, cut/paste, group, delete and
     * undo/redo). Keeps Redux as the single source of truth for the slots.
     */
    setBoardSlots: (state, { payload }: PayloadAction<CardSlot[]>) => {
      state.slots = payload as BoardStudioState['slots']
    },

    /**
     * Replace an existing card in the tree (edit flow).
     */
    updateBoardSlotInTree: (
      state,
      { payload }: PayloadAction<{ cardPath: string[]; updatedSlot: CardSlot }>,
    ) => {
      if (!state.slots) return
      state.slots = updateLevelByPath(
        state.slots as CardSlot[],
        payload.cardPath,
        (level) =>
          level.map((s) =>
            s.id === payload.updatedSlot.id ? payload.updatedSlot : s,
          ),
      ) as BoardStudioState['slots']
    },

    /**
     * Fill a previously empty slot with new card data (create flow).
     */
    fillEmptyBoardSlot: (
      state,
      {
        payload,
      }: PayloadAction<{
        cardPath: string[]
        slotId: string
        newSlot: CardSlot
      }>,
    ) => {
      if (!state.slots) return
      state.slots = updateLevelByPath(
        state.slots as CardSlot[],
        payload.cardPath,
        (level) =>
          level.map((s) =>
            s.id === payload.slotId
              ? { ...payload.newSlot, id: payload.slotId }
              : s,
          ),
      ) as BoardStudioState['slots']
    },

    /**
     * Called on board unmount — clears the working copy so a fresh
     * GET is used next time the board is opened.
     */
    clearBoardSlots: (state) => {
      state.slots = null
    },
  },
})

export const {
  setCurrentBoard,
  clearCurrentBoard,
  initBoardSlots,
  setBoardSlots,
  updateBoardSlotInTree,
  fillEmptyBoardSlot,
  clearBoardSlots,
} = boardStudioSlice.actions

export default boardStudioSlice.reducer
